/**
 * "Wise review queue" — `mockups/studio/money.html` §wise, the ochre card.
 *
 * The rows come from `payments_list_unmatched` rather than from
 * `billing_summary.wise_queue`, and that is the point of the second read: matching a
 * credit invalidates `billing`, which refetches this list, and the card a person just
 * placed disappears from under their cursor without the whole page reloading. The
 * summary's own copy is the fallback while that read is in flight, so the queue is
 * never empty on a first paint that has one.
 *
 * `ReviewQueueItem` owns one credit — the buttons, the allocation editor and the two
 * dialogs. This section is the card round them and the fold under it.
 *
 * **The mockup's match-rate line is not here.** "71% of Wise credits match without a
 * human — 10 of 14 over the last 90 days" needs a count of credits that matched
 * automatically, and nothing on the wire carries one; `UnmatchedPaymentSchema` is by
 * definition only the ones that did not. The report asks for it.
 */

import type { StudioMoneyView } from '@fareast/shared'

import { ReviewQueueItem } from '../../../components/studio/ReviewQueueItem.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { Answer, Mono, Prose } from '../../../components/portal/Text.js'
import type { NameOf } from './names.js'

export type WiseSectionProps = {
  readonly queue: StudioMoneyView['wise_queue']
  readonly nameOf: NameOf
}

export function WiseSection({ queue, nameOf }: WiseSectionProps) {
  return (
    <Section id="wise">
      <SectionHead
        title="Wise review queue"
        note="Credits that did not auto-match — reference, then exact amount, then a human"
      />

      <Stack gap="loose">
        {queue.length === 0 ? (
          <Empty>Every credit has been placed. Nothing is waiting on a person.</Empty>
        ) : (
          <>
            <Answer>
              {`${String(queue.length)} ${queue.length === 1 ? 'credit is' : 'credits are'} waiting on a person. Every one of them arrived with a reference that could not be read, an amount that matched nothing exactly, or a sender nobody recognises.`}
            </Answer>

            <Card need>
              <CardBody tight>
                {queue.map((payment) => (
                  <ReviewQueueItem
                    key={payment.id}
                    payment={payment}
                    clientName={payment.client === null ? undefined : nameOf(payment.client)}
                  />
                ))}
              </CardBody>
            </Card>
          </>
        )}

        <Fold summary="How a credit is matched">
          <Prose>
            Matching order: the invoice reference in the transfer message, then an exact amount and
            currency against exactly one open invoice for that client, then here. Every credit is
            deduped on <Mono>step_id</Mono>, so a replayed webhook cannot double-count.
          </Prose>
          <Prose>
            Placing one by hand is all-or-nothing in one respect: the allocations and what is held
            on account have to come to the credit exactly. Money that is ours and is not any one
            invoice&rsquo;s is held on account and can be spent by a later match; money nobody can
            place is parked, with a note saying what was tried.
          </Prose>
        </Fold>
      </Stack>
    </Section>
  )
}
